<template>
    <div class="modal-content">
        <div class="modal-contentWrap">
            <div class="modal-header bg-1">
                <modal-close />
                <div class="modal-title mb-0 text-center">
                    <h2 class="h3 mb-0">
                        {{ merchant.name }}
                    </h2>
                    <p class="mb-0">
                        {{ subscription && subscription.promo.name }}
                    </p>
                </div>
            </div>
            <div class="modal-body">
                <alt-ean-disclaimer
                    v-if="$store.getters.isWeight(promotion.type)"
                    class="mb-3"
                />

                <div
                    v-if="!hasProductSets"
                    class="body-header d-flex mb-3 gap-1"
                >
                    <div class="body-header-searcher form-group mb-0">
                        <search-input
                            class="mb-0 typo--md"
                            :search-input="searchEanOrName"
                            :search-params-tooltip="['nazwie produktu', 'kodzie EAN']"
                            @change="searchEanOrName = $event"
                        ></search-input>
                    </div>

                    <div
                        v-if="userCanEdit"
                        class="body-header-options"
                    >
                        <money-input
                            class="mb-0"
                            :price="massPrice"
                            :label="`Cena ${
                                hasProductSets ? 'zestawu' : 'w promocji'
                            }`"
                            :disabled="false"
                            style="max-width: 9rem"
                            @onInput="massPrice = $event"
                        />

                        <the-btn
                            :disabled="!massPrice"
                            @click.native="applyMassPrice"
                        >Zastosuj dla wszystkich</the-btn>
                    </div>
                </div>

                <!-- Products table -->
                <div class="c-table shop-prices">
                    <div class="c-thead gap-1">
                        <div class="c-td">
                            {{ hasProductSets ? 'Zestaw' : 'Produkt' }}
                        </div>
                        <div v-if="isWeight" class="c-td">Kod lokalny</div>
                        <div v-if="promoType === 'Promo::Quota'" class="c-td">
                            Rabat brutto
                        </div>
                        <div
                            v-else-if="promoType === 'Promo::BondedQuota'"
                            class="c-td"
                        >
                            Rabat brutto
                            <span class="nowrap">do zestawu</span>
                        </div>
                        <div
                            v-else-if="promoType === 'Promo::BondedPercentage'"
                            class="c-td"
                        >
                            Rabat %
                            <span class="nowrap">do zestawu</span>
                        </div>
                        <div v-else class="c-td">
                            {{
                                hasProductSets
                                    ? 'Cena zestawu'
                                    : 'Cena w promocji'
                            }}
                        </div>
                    </div>

                    <!-- Table body -->
                    <div class="c-tbody">
                        <template v-if="productsSliced.length">
                            <product-tree
                                :promotion="promotion"
                                :products="productsSliced"
                                :apply-mass-price="applyMassPrice"
                                :merchant-id="merchant.id"
                                component-name="ChangeShopPricesProduct"
                            />
                        </template>
                        <template v-else>
                            <div class="c-tr">
                                <div class="c-td">Brak wyników</div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
            <div
                class="modal-footer bg-1 pt-3 d-flex justify-content-between"
                style="position: sticky; bottom: 0"
            >
                <the-pagination-small
                    :page="toString(pagination.page)"
                    :getter="pagination"
                    :set-method="setPagination"
                />

                <v-popover
                    v-if="userCanEdit && noLocalCodes.length"
                    offset="8"
                    class="mt-2 ml-lg-auto ml-0"
                    :container="false"
                >
                    <the-btn
                        :disabled="
                            errors.length > 0 || !isChanged.length || loader
                        "
                    >Zapisz
                        <button-spinner
                            v-if="loader"
                            class="ml-3"
                            status="loading"
                        />
                    </the-btn>

                    <template slot="popover">
                        <popover-confirm
                            :message="noLocalCodesPopoverText"
                            abort-text="Pomiń i modyfikuj"
                            confirm-text="Uzupełnij kody"
                            @aborted="updateSubscription"
                        />
                    </template>
                </v-popover>
                <the-btn
                    v-else-if="userCanEdit"
                    :disabled="errors.length > 0 || !isChanged.length || loader"
                    @click.native="updateSubscription"
                >Zapisz
                    <button-spinner
                        v-if="loader"
                        class="ml-3"
                        status="loading"
                    />
                </the-btn>

                <div
                    v-if="updateError"
                    class="w-100 text-sm text-danger text-right"
                >
                    Zmiana cen nie powiodła się.
                    Napisz na <a href="mailto:bok@europlatform.pl">bok@europlatform.pl</a>
                    lub zadzwoń na numer <a href="tel:48221291400">+48 22 12 91 400</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import promoBonded from '@/mixins/promoBonded'
import AltEanDisclaimer from '@/components/promotions/altEans/AltEanDisclaimer'

export default {
    mixins: [promoBonded],

    components: {
        AltEanDisclaimer
    },

    data: function() {
        return {
            pagination: {
                nextPage: '',
                page: 1,
                perPage: 10,
                prevPage: '',
                totalPages: '',
                totalRecords: ''
            },
            initialState: null,
            searchEanOrName: '',
            massPrice: null,
            queueTimer: null,
            loader: false,
            noLocalCodes: [],
            updateError: false
        }
    },
    computed: {
        modalArgs() {
            return this.$store.getters.modalState.args
        },
        merchant() {
            return this.$store.getters.getMerchantById(
                this.modalArgs.merchantId
            )
        },
        subscription() {
            if (!this.modalArgs) {
                return {}
            }

            return this.$store.getters.getSubscriptionByMerchantId(
                this.modalArgs.merchantId
            )
        },
        promoType() {
            return this.subscription.promo.type
        },
        promotion() {
            return this.modalArgs.promotion
        },
        products() {
            const products = this.subscription.product_subscriptions
                .map(product => {
                    const promoProduct = this.subscription.promo.products.find(
                        el => el.id === product.product_id
                    )
                    const dictProduct = promoProduct.dict_product
                    return {
                        id: product.id,
                        productId: product.product_id,
                        price: product.price,
                        name: dictProduct.name,
                        ean: promoProduct.ean || product.ean || dictProduct.ean,
                        alternative_ean: product.alternative_ean,
                        discount_amount: promoProduct.discount_amount,
                        max_price: promoProduct.maximum_promotional_price,
                        promo_dict_product_id:
                            promoProduct.promo_dict_product_id,
                        eanErrors: product.eanErrors,
                        eanPresent: promoProduct.ean_present,
                        main: promoProduct.main,
                        linked_products_count:
                            promoProduct.linked_products_count
                    }
                })
                .filter(product => {
                    const search = this.searchEanOrName.toLowerCase()
                    const name = product.name.toString().toLowerCase()
                    const ean = product.ean.toString().toLowerCase()
                    const localEan =
                        product.alternative_ean?.toString().toLowerCase() || ''
                    return (
                        name.includes(search) ||
                        ean.includes(search) ||
                        localEan.includes(search)
                    )
                })

            return products
        },
        productSets() {
            const gSets = this.$store.getters.getPromoProductSets
            const sets = this.subscription.product_set_subscriptions.map(
                set => {
                    const setsLength =
                        this.subscription.product_set_subscriptions.length
                    const setNumber = setsLength > 1 ? setsLength : ''

                    const name = `Zestaw produktów ${
                        this.isBonded ? 'rabatowanych' : 'promocyjnych'
                    } ${setNumber}`

                    let price
                    if (this.promotion.type === 'Promo::BondedQuota' && gSets) {
                        const pSet = gSets.find(
                            el => el.id === set.product_set_id
                        )
                        price = -(pSet.discount_amount * +pSet.required_weight)
                    } else if (
                        this.promotion.type === 'Promo::BondedPercentage' &&
                        gSets
                    ) {
                        const pSet = gSets.find(
                            el => el.id === set.product_set_id
                        )
                        price = -pSet.discount_percentage
                    } else {
                        price = set.price
                    }

                    const gSet = gSets.find(el => el.id === set.product_set_id)

                    return {
                        id: set.id,
                        price,
                        name,
                        ean: '',
                        max_price: gSet.price,
                        main: true
                    }
                }
            )
            return sets
        },
        productsSliced() {
            const { pagination } = this

            // If products is empty, use productSets
            const products = this.products.length
                ? this.products.filter(p => p.main)
                : this.productSets

            // Calculate the slice indices based on pagination (page number and items per page)
            const startIndex = (pagination.page - 1) * pagination.perPage
            const endIndex = pagination.page * pagination.perPage
            const mainSlicedProducts = products.slice(startIndex, endIndex)

            // Create a Set of promo_dict_product_id from the main sliced products for quick lookup
            const promoIdsSet = new Set(
                mainSlicedProducts.map(p => p.promo_dict_product_id)
            )

            /**
             * Filter non-main products that have the same
             * promo_dict_product_id as the main sliced products
             */
            const noMainSlicedProducts = this.products.filter(
                p => !p.main && promoIdsSet.has(p.promo_dict_product_id)
            )

            // Combine the main sliced products and the matching non-main products
            return [...mainSlicedProducts, ...noMainSlicedProducts]
        },

        hasProductSets() {
            return this.$store.getters.hasProductSets(
                this.subscription.promo.type
            )
        },

        isChanged() {
            if (!this.initialState) return false
            const { initialState, subscription } = this

            const productsKey = this.hasProductSets
                ? 'product_set_subscriptions'
                : 'product_subscriptions'

            return subscription[productsKey].filter(prodNew => {
                const prodInit = initialState[productsKey].find(
                    prod => prod.id === prodNew.id
                )

                const eanChanged =
                    (prodNew.alternative_ean || prodInit.alternative_ean) &&
                    prodNew.alternative_ean !== prodInit.alternative_ean

                const priceChanged =
                    Number(prodNew.price) !== Number(prodInit.price)

                // Return true if either EAN or price has changed
                return eanChanged || priceChanged
            })
        },

        isPromoEditPermission() {
            return this.$store.getters.isPromoEditPermission
        },

        isEditablePromo() {
            return this.$store.getters.isEditablePromo(this.promotion.type)
        },

        userCanEdit() {
            return this.isEditablePromo && this.isPromoEditPermission
        },

        isWeight() {
            return !this.promotion.all_eans_present && !this.hasProductSets
        },

        noLocalCodesPopoverText() {
            return this.$store.getters.isWeight(this.promoType)
                ? 'Modyfikujesz promocję wagową. Upewnij się, że kody lokalne towarów wagowych zostały poprawnie przypisane, w przeciwnym razie promocja może nie działać poprawnie.'
                : 'Promocja zawiera kody lokalne. Upewnij się, że zostały one poprawnie przypisane. W przeciwnym razie promocja może nie działać prawidłowo.'
        },

        errors() {
            return this.$store.getters.getSubscriptionLocalCodeErrors
        }
    },
    methods: {
        applyMassPrice() {
            this.subscription.product_subscriptions.forEach(product => {
                product.price = (+this.massPrice).toFixed(2)
            })
            this.validate()
        },

        validate() {
            const products = this.products.length
                ? this.products
                : this.productSets

            const errors = []
            const noLocalCodes = []

            // Check only main products
            const mainProducts = products.filter(product => product.main)

            mainProducts.forEach(product => {
                const price = +product.price
                const maxPrice = +product.max_price

                if (price === 0 || price > maxPrice) {
                    // Check if price is zero or greater than the max price, and log an error if so
                    errors.push({ id: product.id, type: 'price' })
                }

                if (!this.isWeight) {
                    // If it's not a weight promo, skip further checks
                    return
                }

                // Check if there's any duplicated alternative ean
                const isDuplicateAlternativeEan =
                    mainProducts.filter(
                        p =>
                            p.alternative_ean === product.alternative_ean &&
                            p.alternative_ean
                    ).length > 1

                if (isDuplicateAlternativeEan) {
                    errors.push({ id: product.id, type: 'code' })
                }

                if (!product.alternative_ean) {
                    // If the product has no alternative EAN, log an error for missing code
                    noLocalCodes.push({ id: product.id, type: 'nocode' })
                }
            })

            this.noLocalCodes = noLocalCodes
            this.$store.dispatch('setSubscriptionLocalCodeErrors', errors)
        },

        setPagination(page) {
            const { pagination } = this

            const products = this.products.length
                ? this.products.filter(p => p.main)
                : this.productSets

            const totalRecords = products.length
            const totalPages = Math.ceil(products.length / pagination.perPage)
            const nextPage = page < totalPages ? page + 1 : null
            const prevPage = page > 1 ? page - 1 : null

            pagination.page = page || 1
            pagination.totalRecords = totalRecords
            pagination.totalPages = totalPages || 1
            pagination.nextPage = nextPage
            pagination.prevPage = prevPage
        },

        resetInitState() {
            this.initialState = JSON.parse(JSON.stringify(this.subscription))
        },

        async updateSubscription() {
            this.updateError = false
            try {
                if (this.errors.length > 0 || !this.isChanged || this.loader) {
                    return
                }

                const altEansToSave = []
                const altEansToUpdate = []

                const pushAltEans = (errors, product) => {
                    const merchantId = this.merchant.id
                    const altEan = product.alternative_ean
                    const dictProdId = this.subscription.promo.products.find(
                        p => p.id === product.product_id
                    ).dict_product_id

                    if (altEan && errors?.length) {
                        const err = errors.find(er => {
                            return (
                                er.alternative_ean === altEan &&
                                er.merchant_id === merchantId
                            )
                        })
                        if (err) {
                            altEansToUpdate.push({
                                id: err.id,
                                alternative_ean: altEan
                            })
                        } else {
                            altEansToSave.push({
                                merchant_id: merchantId,
                                alternative_ean: altEan,
                                dict_product_id: dictProdId
                            })
                        }
                    } else if (altEan) {
                        altEansToSave.push({
                            merchant_id: merchantId,
                            alternative_ean: altEan,
                            dict_product_id: dictProdId
                        })
                    }
                }

                const { group } = this.modalArgs

                const products = this.subscription.product_subscriptions.map(
                    p => {
                        const { price, alternative_ean, id } = p

                        pushAltEans(p.eanErrors, p)

                        return {
                            id,
                            price: parseFloat(price),
                            alternative_ean: alternative_ean || ''
                        }
                    }
                )

                // eslint-disable-next-line no-unused-vars
                const product_sets =
                    this.subscription.product_set_subscriptions.map(p => {
                        const { price, id } = p

                        return {
                            id,
                            price: parseFloat(price)
                        }
                    })

                const subscription = {
                    id: this.subscription.id,
                    merchant_id: this.subscription.merchant_id,
                    product_sets,
                    products,
                    promo_id: this.subscription.promo_id,
                    state: this.subscription.state
                }

                const groups = [
                    {
                        acceptance: group.acceptance,
                        id: group.id,
                        merchant_ids: [this.subscription.merchant_id],
                        subscriptions: [subscription]
                    }
                ]

                const payload = {
                    userId: this.$store.getters.userId,
                    state: 'update',
                    groups
                }

                if (altEansToSave.length) {
                    this.$store.$api.post('saveMerchantAlternativeEans', {
                        save: altEansToSave
                    })
                }

                this.loader = true
                const patch = await this.$store.$api.patch(
                    'subscriptionUpdate',
                    payload
                )

                const response = patch.data.data
                const queueId = response.queue_id

                this.$store.commit('SET_SUBSCRIPTIONS_QUEUEID', queueId)

                const checkProgress = async() => {
                    const res = await this.$store.$api.get(
                        `subscriptionQueueProgress/${queueId}`
                    )

                    return res.data.data
                }

                setTimeout(() => {
                    startInterval()
                }, 1000)

                const startInterval = () => {
                    this.queueTimer = setInterval(async() => {
                        const { status, processed, total } = await checkProgress()

                        if (status === 'waiting' || status === 'processing') {
                            // Do nothing, data is still processing
                            return
                        }

                        // Not processing anymore; clear the timer
                        this.clearTimer()

                        if (status === 'error' || processed !== total) {
                            // Show error notification
                            this.updateError = true

                            this.loader = false
                            return
                        }

                        // All good, accepted and error subscriptions
                        const accepted = await this.$store.dispatch(
                            'fetchProcessedSubscriptions',
                            {
                                queueId,
                                type: 'successed',
                                status: 'accepted'
                            }
                        )

                        if (accepted.data.data.accepted.count > 0) {
                            this.$store.dispatch('openModal', {
                                component: 'ModalEditPromoSummary'
                            })

                            this.loader = false
                            return
                        }

                        const errors = await this.$store.dispatch(
                            'fetchProcessedSubscriptions',
                            {
                                type: 'error',
                                status: 'error'
                            }
                        )

                        this.loader = false

                        if (!errors.data?.data?.error?.count) {
                            throw new Error(errors?.data?.message || 'Error fetchProcessedSubscriptions')
                        }

                        let errorText = 'Wystąpił błąd. Spróbuj ponownie później.'
                        const errorPromos = errors.data.data.error.promos || []
                        if (errorPromos.length > 0) {
                            const firstPromoError = errorPromos[0].description

                            if (firstPromoError.trim() !== '') {
                                errorText = firstPromoError
                            }
                        }

                        this.$store.dispatch('addNotification', {
                            type: 'danger',
                            content: errorText
                        })

                        this.updateError = true
                    }, 1000)
                }
            } catch (error) {
                this.loader = false
                this.$store.dispatch('addNotification', {
                    type: 'danger',
                    content: 'Wystąpił błąd. Spróbuj ponownie później.'
                })
                console.log(error)
            }
        },

        clearTimer() {
            clearInterval(this.queueTimer)
            this.queueTimer = null
        }
    },

    beforeDestroy() {
        this.clearTimer()

        this.subscription.product_subscriptions = this.initialState.product_subscriptions
        this.subscription.product_set_subscriptions = this.initialState.product_set_subscriptions
    },

    created() {
        this.setPagination(1)

        this.resetInitState()
    },

    watch: {
        searchEanOrName() {
            this.setPagination(1)
        },
        'subscription.product_set_subscriptions': {
            immediate: true,
            deep: true,
            handler() {
                this.validate()
            }
        },
        'subscription.product_subscriptions': {
            immediate: true,
            deep: true,
            handler() {
                this.validate()
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import 'node_modules/bootstrap/scss/functions';
@import 'node_modules/bootstrap/scss/variables';
@import 'node_modules/bootstrap/scss/mixins';

.body-header {
    @include media-breakpoint-down(md) {
        flex-direction: column;
    }
}
.body-header-searcher {
    min-width: 300px;
}

.body-header-options {
    display: flex;
    gap: 1rem;
    width: 100%;
    justify-content: flex-end;
    align-items: start;
}
</style>
